import { tns } from 'tiny-slider/src/tiny-slider'

import { chooseSrcByWidth, parseSrcset } from 'shared/utils/imageUtils'
import {
  HIDDEN_MEDIUM_CLASS,
  NO_OVERFLOW_CLASS,
} from 'views/assets/scripts/consts'
import { MAX_PRODUCT_IMAGE_SIZE } from 'views/consts/product-image-sizes'
import { isSrcSetSupported } from 'views/utils/browserFeatureDetection'
import createA11Dialog from 'views/utils/createA11Dialog'
import debounce from 'views/utils/debounce'

import {
  GALLERY_MAJOR_SELECTOR,
  MODAL_CONTENT_SELECTOR,
  MODAL_GALLERY_CONTROLS_SELECTOR,
  MODAL_GALLERY_NEXT_SELECTOR,
  MODAL_GALLERY_PREV_SELECTOR,
  MODAL_SHOW_TRIGGER_SELECTOR,
  NAV_SMALLSCREEN_SELECTOR,
  OVERLAY_SELECTOR,
  RED_POINT_SELECTOR,
  VIDEO_BADGE_SELECTOR,
  VIDEO_CONTAINER_SELECTOR,
  VIDEO_PLAY_SELECTOR,
  VIDEO_SLIDE_DATA_SELECTOR,
  VIDEO_THUMBNAIL_SELECTOR,
} from './consts'
import {
  buildScopedModalSelector,
  getSliderBaseOptions,
  updateHeight,
} from './helpers'

export default class Modal {
  constructor(ctx) {
    this.modalContent = ctx.galleryElement.querySelector(MODAL_CONTENT_SELECTOR)
    this.modalNav = ctx.galleryElement
      .querySelector(NAV_SMALLSCREEN_SELECTOR)
      .cloneNode(true)

    this.modalMajorImages = ctx.galleryElement
      .querySelector(GALLERY_MAJOR_SELECTOR)
      .cloneNode(true)
    // fixme this clones i.a. a RedPointsBubble with a onClick modal
    //       we refrain from coping the event listeners as a modal opening in a modal
    //       is bad practice and causes a lot of trouble and edge-case aware scripts
    this.galleryOverlay = ctx.galleryElement.querySelector(OVERLAY_SELECTOR)
    this.modalGalleryOverlay = this.galleryOverlay
      ? this.galleryOverlay.cloneNode(true)
      : null

    this.modalId = ctx.galleryElement.dataset.modalId
    this.modalDialog = null
    this.scopedModalSelector = buildScopedModalSelector(this.modalId)
    this.debounceTime = 250

    // this is true until after the first image is shown in modal slider
    this.initialModalLoad = true

    this.init(ctx)
  }

  init = ctx => {
    this.prepareModalDOM()
    this.initModalDialog(ctx)
    this.attachShowModalClickHandlers(ctx)
    this.attachPlayVideoClickHandlers(ctx)
  }

  attachPlayVideoClickHandlers = ctx => {
    const videoElements = [
      ...ctx.galleryElement.querySelectorAll(VIDEO_CONTAINER_SELECTOR),
    ]

    Array.prototype.forEach.call(videoElements, videoElement => {
      const thumbnail = videoElement.querySelector(VIDEO_THUMBNAIL_SELECTOR)
      thumbnail.onclick = () => ctx.video.play(videoElement)
    })

    return videoElements
  }

  attachShowModalClickHandlers = ctx => {
    const modalShowTriggers = ctx.galleryElement.querySelectorAll(
      MODAL_SHOW_TRIGGER_SELECTOR
    )

    let firstVideoIndex = null

    Array.prototype.forEach.call(
      modalShowTriggers,
      (modalShowTrigger, index) => {
        const isVideo = modalShowTrigger.dataset.clientsideHook
          ? modalShowTrigger.dataset.clientsideHook.includes(
              VIDEO_SLIDE_DATA_SELECTOR
            )
          : false

        if (!firstVideoIndex && isVideo) {
          firstVideoIndex = index
        }

        modalShowTrigger.onclick = this.showModal({
          shouldPlayVideo: isVideo,
          ctx,
          index,
        })
      }
    )

    if (firstVideoIndex) {
      const videoButtons =
        ctx.galleryElement.querySelectorAll(VIDEO_PLAY_SELECTOR)

      Array.prototype.forEach.call(videoButtons, videoButton => {
        videoButton.onclick = this.videoButtonClick(ctx, firstVideoIndex)
      })
    }

    return modalShowTriggers
  }

  initModalDialog = ctx => {
    this.modalDialog = createA11Dialog({
      element: document.getElementById(this.modalId),
    })

    const onModalShow = (event, index) => this.onModalShow(ctx, index)
    const onModalHide = () => this.onModalHide(ctx)

    this.modalDialog.on('show', onModalShow)
    this.modalDialog.on('hide', onModalHide)

    return this.modalDialog
  }

  prepareModalDOM = () => {
    const modalGalleryContainer = document.createElement('div')
    const container = this.modalMajorImages
    const images = [...container.querySelectorAll('img')]

    this.modalNav.classList.remove(HIDDEN_MEDIUM_CLASS)
    this.modalContent.appendChild(container)

    if (!isSrcSetSupported()) {
      images.forEach(image => {
        image.src = chooseSrcByWidth(
          parseSrcset(image.getAttribute('srcset')),
          MAX_PRODUCT_IMAGE_SIZE
        )
      })
    }

    const majorSlider = this.modalContent.querySelector(GALLERY_MAJOR_SELECTOR)
    if (majorSlider) {
      const sliderItems = [...majorSlider.querySelectorAll('li')]
      Array.prototype.forEach.call(sliderItems, sliderItem => {
        const itemLink = sliderItem.querySelector('a')
        if (itemLink) {
          const itemChildren = itemLink.firstChild.cloneNode(true)
          sliderItem.replaceChild(itemChildren, itemLink)
        }
      })
    }

    modalGalleryContainer.appendChild(this.modalMajorImages)

    if (this.modalGalleryOverlay) {
      const videoBadge =
        this.modalGalleryOverlay.querySelector(VIDEO_BADGE_SELECTOR)
      if (videoBadge) {
        videoBadge.remove()
      }
      const redPointButton =
        this.modalGalleryOverlay.querySelector(RED_POINT_SELECTOR)
      if (redPointButton) {
        const redPointLabel = redPointButton.firstChild.cloneNode(true)
        redPointButton.remove()
        this.modalGalleryOverlay.appendChild(redPointLabel)
      }
      modalGalleryContainer.appendChild(this.modalGalleryOverlay)
    }
    this.modalContent.appendChild(modalGalleryContainer)
    this.modalContent.appendChild(this.modalNav)

    return {
      modalNav: this.modalNav,
      modalContent: this.modalContent,
      modalGalleryContainer,
    }
  }

  showModal =
    ({ shouldPlayVideo, ctx, index }) =>
    event => {
      event.preventDefault()

      if (!ctx.isLargeViewport) {
        return
      }

      this.modalDialog.show(index)

      if (shouldPlayVideo) {
        ctx.video.playVideo(ctx, index)
      }
    }

  videoButtonClick = (ctx, index) => event => {
    event.preventDefault()

    if (ctx.isLargeViewport) {
      this.modalDialog.show(index)
    }

    ctx.video.playVideo(ctx, index)
  }

  onModalHide = ctx => {
    document.body.classList.remove(NO_OVERFLOW_CLASS)
    ctx.video.pause()

    if (ctx.majorSlider?.getInfo()) {
      const majorSliderInfo = ctx.majorSlider.getInfo()
      const currentImage = majorSliderInfo.slideItems[
        majorSliderInfo.index
      ].querySelector(MODAL_SHOW_TRIGGER_SELECTOR)

      if (currentImage) {
        currentImage.blur()
      }
    }
  }

  onModalShow = (ctx, index) => {
    if (!ctx.modalSlider) {
      this.initModalSliderOnFirstModalLoad(ctx, index)
      document.body.classList.add(NO_OVERFLOW_CLASS)
    } else {
      this.modalSliderShowCurrentSlideFromMajorSlider(ctx, index)
    }

    if (ctx.modalSlider.getInfo) {
      ctx.modalSlider.getInfo().controlsContainer.focus()
    }
    document.body.classList.add(NO_OVERFLOW_CLASS)

    updateHeight(this.modalContent.querySelector(GALLERY_MAJOR_SELECTOR))
  }

  modalSliderShowCurrentSlideFromMajorSliderOnFirstLoad = (ctx, index) => {
    if (!ctx.modalSlider.events) {
      return
    }

    if (this.initialModalLoad) {
      this.initialModalLoad = false
      ctx.modalSlider.goTo(index)
    }
  }

  modalSliderShowCurrentSlideFromMajorSlider = (ctx, index) => {
    ctx.modalSlider.goTo(index)
    ctx.modalSlider.updateSliderHeight()
  }

  initModalSliderOnFirstModalLoad = (ctx, index) => {
    ctx.modalSlider = tns({
      ...getSliderBaseOptions(),
      container: this.modalMajorImages,
      navContainer: this.modalNav,
      controlsContainer: this.scopedModalSelector(
        MODAL_GALLERY_CONTROLS_SELECTOR
      ),
      prevButton: this.scopedModalSelector(MODAL_GALLERY_PREV_SELECTOR),
      nextButton: this.scopedModalSelector(MODAL_GALLERY_NEXT_SELECTOR),
    })

    window.addEventListener(
      'resize',
      debounce(ctx.modalSlider.updateSliderHeight, this.debounceTime)
    )

    this.registerOnChangeEvent(ctx)

    this.modalSliderShowCurrentSlideFromMajorSliderOnFirstLoad(ctx, index)
  }

  registerOnChangeEvent = ctx => {
    const onChangeEvents = () => {
      ctx.video.pause()
      ctx.handleRedPointDisplay(ctx.modalSlider, this.modalGalleryOverlay)
    }

    if (ctx.modalSlider?.events) {
      ctx.modalSlider.events.on('indexChanged', onChangeEvents)
    }
  }
}
